import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getArticles, getKnowledgeCategories, addArticleToFavorites, removeArticleFromFavorites, getFavoriteArticles } from '../api/knowledge';
import { toast } from 'sonner';
import ArticleCard from '../components/knowledge/ArticleCard';
import CreateTicketModal from '../components/knowledge/CreateTicketModal';
import CreateArticleModal from '../components/knowledge/CreateArticleModal';
import FloatingHelpButton from '../components/knowledge/FloatingHelpButton';
import EmptyState from '../components/knowledge/EmptyState';
import { ArticleListSkeleton } from '../components/knowledge/LoadingSkeleton';
import { BookOpen, Grid3x3, List, PenTool, Sparkles, Search } from 'lucide-react';

const KnowledgeBase = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [favorites, setFavorites] = useState([]); 

  // Modals
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [ticketInitial, setTicketInitial] = useState({ subject: '', description: '', articleId: null });

  const canCreateArticle = user?.role === 'admin' || user?.role === 'employee';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [articlesRes, categoriesRes, favRes] = await Promise.all([
        getArticles({ category_id: selectedCategory, search: searchTerm }),
        getKnowledgeCategories(),
        getFavoriteArticles()
      ]);

      if (articlesRes.success) setArticles(articlesRes.data.articles || []);
      if (categoriesRes.success) setCategories(categoriesRes.data || []);
      if (favRes.success) setFavorites(favRes.data?.map(f => f.id) || []);
      
    } catch (error) {
      console.error('Failed to load KB data:', error);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await getArticles({ search: searchTerm, category_id: selectedCategory });
      if (res.success) setArticles(res.data.articles || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const trendingArticles = useMemo(() => {
    if (searchTerm || selectedCategory) return [];
    return [...articles]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3);
  }, [articles, searchTerm, selectedCategory]);

  const displayArticles = useMemo(() => {
    if (searchTerm || selectedCategory) return articles;
    const trendingIds = trendingArticles.map(a => a.id);
    return articles.filter(a => !trendingIds.includes(a.id));
  }, [articles, trendingArticles, searchTerm, selectedCategory]);

  const handleFavoriteToggle = async (article) => {
    const isFav = favorites.includes(article.id);
    setFavorites(prev => isFav ? prev.filter(id => id !== article.id) : [...prev, article.id]);
    
    try {
      if (isFav) {
        await removeArticleFromFavorites(article.id);
        toast.success('Removed from favorites');
      } else {
        await addArticleToFavorites(article.id);
        toast.success('Added to favorites');
      }
    } catch (err) {
      setFavorites(prev => isFav ? [...prev, article.id] : prev.filter(id => id !== article.id));
      toast.error('Failed to update favorites');
    }
  };

  const openTicketFromArticle = (article) => {
    setTicketInitial({
      subject: article.title,
      description: `Reference Article: ${article.title} (ID: ${article.id})\n\n${article.short_description || ''}`,
      articleId: article.id,
    });
    setTicketModalOpen(true);
  };

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 pb-20">
        
        {/* === COMPACT STICKY HEADER === */}
        <div className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-800 transition-all">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              
              {/* Left: Icon & Title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    Knowledge Base
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                    Browse guides and documentation
                  </p>
                </div>
              </div>

              {/* Right: Search, view toggles, write button */}
              <div className="flex items-center gap-3 flex-1 md:flex-none md:min-w-[420px]">
                {/* Search Bar - compact inline */}
                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch();
                    }}
                    placeholder="Search articles..."
                    className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-slate-800 border border-transparent rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:bg-white dark:focus:bg-slate-900 transition-all"
                  />
                </div>

                {/* View toggles */}
                <div className="flex bg-gray-100 dark:bg-slate-800 p-1 rounded-lg shrink-0">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'}`}
                    title="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'}`}
                    title="Grid view"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                </div>

                {/* Write button */}
                {canCreateArticle && (
                  <button
                    onClick={() => setArticleModalOpen(true)}
                    className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-indigo-500/30"
                  >
                    <PenTool className="w-3.5 h-3.5" />
                    Write
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs directly under header */}
        <div className="w-full px-4 sm:px-6 lg:px-8 pt-4">
          <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('')}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                selectedCategory === ''
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800'
                  : 'bg-transparent text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-slate-800'
              }`}
            >
              All Topics
            </button>
            <div className="w-px h-4 bg-gray-300 dark:bg-slate-700 mx-1 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  selectedCategory === cat.id
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800'
                    : 'bg-transparent text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* ================= MAIN CONTENT ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 mt-6 space-y-8">
          
          {loading ? (
            <div className="mt-8"><ArticleListSkeleton count={6} /></div>
          ) : (
            <>
              {/* Trending Section - Subtle & Modern */}
              {trendingArticles.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-amber-500 fill-current" />
                    <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Popular Now
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {trendingArticles.map(article => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        isTrending={true}
                        viewMode="grid"
                        isFavorite={favorites.includes(article.id)}
                        onFavoriteToggle={() => handleFavoriteToggle(article)}
                        onHelpfulToggle={() => fetchData()}
                        onClick={() => openTicketFromArticle(article)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Main List */}
              <section>
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100 dark:border-slate-800">
                  <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {selectedCategory ? 'Category Results' : 'Recent Articles'}
                  </h2>
                  <span className="text-xs text-gray-400">
                    Showing {displayArticles.length} results
                  </span>
                </div>

                {displayArticles.length === 0 ? (
                  <div className="mt-8">
                    <EmptyState
                      icon={BookOpen}
                      title="No articles found"
                      description={searchTerm || selectedCategory ? 'Try adjusting your search terms or filters.' : 'No articles in the knowledge base yet.'}
                      hint={!searchTerm && !selectedCategory && canCreateArticle ? 'Run backend/database/migrations/seed_knowledge_base_articles.sql to add sample articles, or use Write to create one.' : undefined}
                    />
                  </div>
                ) : (
                  <div className={viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
                    : 'flex flex-col gap-3'}>
                    {displayArticles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        viewMode={viewMode}
                        isFavorite={favorites.includes(article.id)}
                        onFavoriteToggle={() => handleFavoriteToggle(article)}
                        onHelpfulToggle={() => fetchData()}
                        onClick={() => openTicketFromArticle(article)}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* Modals */}
        <CreateTicketModal
          isOpen={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          initialSubject={ticketInitial.subject}
          initialDescription={ticketInitial.description}
          knowledgeArticleId={ticketInitial.articleId}
        />

        <CreateArticleModal
          isOpen={articleModalOpen}
          onClose={() => setArticleModalOpen(false)}
          onSuccess={() => {
            fetchData();
            setArticleModalOpen(false);
          }}
        />

        <FloatingHelpButton />
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeBase;